import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { differenceInDays, parse } from 'date-fns';
import {
  ConfigBaseFilter,
  ConfigService,
  ListLayout,
  ListLayoutMultipleComponent,
  ListSortOption,
  SimpleConfigBaseFilter
} from 'gung-list';
import {
  Activity,
  ActivityService,
  AuthService,
  Customer,
  CustomerService,
  MetadataService,
  SelectedCustomerService
} from 'gung-standard';
import { forkJoin, Observable, of } from 'rxjs';
import { first, map, mergeMap, switchMap } from 'rxjs';
import { HlDisplayMeetingCardListComponent } from '../components/hl-display-meetings/hl-display-meeting-card-list/hl-display-meeting-card-list.component';
import { joinCustomersOntoMeetings } from './hl-display-customer-join';
import { HlSalesPersonFilter } from './filters/hl-display-sales-filters';
import { HlDisplayPortalFeaturesService, OrderSearchSettingsGroup } from './hl-display-portal-features.service';

export class MeetingDateFilter extends SimpleConfigBaseFilter<Activity> {
  customers: { [s: string]: Customer };
  constructor(protected translateService: TranslateService) {
    super();
  }

  getName(): string {
    return 'MEETING_DATE';
  }

  getOptionIds(item: Activity): string[] {
    const dayDifferense = differenceInDays(new Date(), new Date(item.extra.hr.eventpldat));

    if (dayDifferense <= 1) {
      return ['TODAY'];
    }
    if (dayDifferense <= 7) {
      return ['THIS_WEEK'];
    }
    if (dayDifferense <= 30) {
      return ['THIS_MONTH'];
    }
    if (dayDifferense <= 90) {
      return ['LAST_THREE_MONTHS'];
    }
    return ['OVER_THREE_MONTHS'];
  }

  getOptionName(key: string): string {
    return this.translateService.instant(key);
  }
}

export class MeetingFinancialGroupFilter extends SimpleConfigBaseFilter<Activity> {
  constructor(protected metadataService: MetadataService) {
    super();
  }

  getName(): string {
    return 'FINANCIAL_GROUP';
  }

  getOptionIds(item: Activity): string[] {
    const metadataValue = this.metadataService.getMetadataValue(
      'q_financialgrp',
      'q_financialgrpdesc',
      item.extra._customer?.extra.kus?.q_financialgrpcode
    );
    if (metadataValue == null) {
      return ['UNDEFINED'];
    } else {
      return [metadataValue];
    }
  }

  getOptionName(key: string): string {
    return key;
  }
}

interface MeetingsFilterGating {
  salesPersonFilterEnabled: boolean;
  /** SALES or CUSTOMER_SERVICE. The internal filters are not shown to anyone else. */
  isSalesOrCustomerService: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class HlDisplayMeetingsListConfigService implements ConfigService<Activity> {
  constructor(
    protected activityService: ActivityService,
    protected translateService: TranslateService,
    protected customerService: CustomerService,
    protected metadataService: MetadataService,
    protected selectedCustomerService: SelectedCustomerService
  ) { }

  protected hlDisplayPortalFeaturesService = inject(HlDisplayPortalFeaturesService);
  protected authService = inject(AuthService);

  topFilter: boolean = false;

  protected salesPersonFilter = new HlSalesPersonFilter<Activity>(
    this.metadataService,
    item => item.extra._customer?.extra?.kus?.saljare
  );

  protected filterGating?: MeetingsFilterGating;

  getItems(): Observable<Activity[]> {
    return forkJoin({
      activities: this.activityService.getAllActivitys().pipe(first()),
      customers: this.customerService.getCustomers().pipe(first()),
      settings: this.hlDisplayPortalFeaturesService.getPortalFeatureGroup('orderSearchSettingsGroup').pipe(first()),
      roles: this.authService.getRoles().pipe(first())
    }).pipe(
      map(({ activities, customers, settings, roles }) => {
        const orderSearchSettings = settings as OrderSearchSettingsGroup;
        this.filterGating = {
          // Defaults true on HLDisplayOrderSearchSettings; treat an absent field as enabled so a
          // frontend-before-backend deploy does not silently drop the filter.
          salesPersonFilterEnabled: orderSearchSettings.salesPersonFilterEnabledActivity !== false,
          isSalesOrCustomerService: roles.includes('SALES') || roles.includes('CUSTOMER_SERVICE')
        };

        return joinCustomersOntoMeetings(activities, customers);
      }),
      map(activities => {
        return activities.sort((a, b) => {
          if (a.extra.hr.eventpldat && a.extra.hr.eventpltime && b.extra.hr.eventpldat && b.extra.hr.eventpltime) {
            const aDate = new Date(
              (a.extra.hr.eventpldat || a.extra.hr.regdat).substr(0, 11) +
              (a.extra.hr.eventpltime || a.extra.hr.regdat).substr(11)
            ).getTime();
            const bDate = new Date(
              (b.extra.hr.eventpldat || b.extra.hr.regdat).substr(0, 11) +
              (b.extra.hr.eventpltime || b.extra.hr.regdat).substr(11)
            ).getTime();

            return bDate - aDate;
          }

          return 0;
        });
      })
    );
  }

  getFilters(): ConfigBaseFilter<Activity>[] {
    const filters: ConfigBaseFilter<Activity>[] = [
      new MeetingDateFilter(this.translateService),
      new MeetingFinancialGroupFilter(this.metadataService)
    ];

    const gating = this.filterGating;
    if (!gating) {
      // Unreachable via the Meetings tab itself - see filterGating. Kept loud rather than silent
      // because an unresolved role check must never be read as "the user has the role": that
      // would expose internal rep assignment to a customer-facing portal user.
      console.warn(
        'HlDisplayMeetingsListConfigService.getFilters() ran before getItems() resolved the portal settings and user roles; rendering no Sales Person filter.'
      );
      return filters;
    }

    if (gating.salesPersonFilterEnabled && gating.isSalesOrCustomerService) {
      filters.push(this.salesPersonFilter);
    }

    return filters;
  }

  getSortOptions(): ListSortOption<Activity>[] {
    // TODO: implement
    return [];
  }

  getBatchSizes(): number[] {
    return [24];
  }

  getLayouts(): ListLayout<Activity>[] {
    return [
      {
        getIconClass: () => '',
        getListItemComponent: () => HlDisplayMeetingCardListComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'Order list'
      }
    ];
  }

  getSearchTerms(item: Activity): string[] {
    return [item.id];
  }

  getItemId(item: Activity): string {
    return item.id;
  }

  getNothingFoundTranslateTag() {
    return 'NOTHING_FOUND';
  }
}
